
import mammoth from 'mammoth';
import * as pdfjs from 'pdfjs-dist';

// Interface for the extracted document content
export interface ExtractedDocument {
  originalFileName: string;
  originalFileType: string;
  originalFileSize: number;
  extractedText: string;
}

// Initialize PDF.js worker
// This function initializes the PDF.js worker when called
const initializePdfWorker = () => {
  if (typeof window !== 'undefined') {
    import('pdfjs-dist/build/pdf.worker.entry')
      .then(pdfjsWorker => {
        // @ts-ignore
        if ('pdfjsLib' in window) {
          // @ts-ignore
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;
        } else {
          // @ts-ignore
          pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;
        }
      })
      .catch(err => console.error('Error loading PDF.js worker:', err));
  }
};

// Initialize the worker
initializePdfWorker();

/**
 * Extract text from PDF files using PDF.js
 */
async function extractTextFromPdf(file: File): Promise<string> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    // Use PDF.js to load the document
    const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
    let extractedText = '';
    
    // Extract text from each page
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(' ');
      extractedText += pageText + '\n';
    }
    
    return extractedText;
  } catch (error) {
    console.error('Error extracting text from PDF:', error);
    throw new Error(`Failed to extract text from PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Extract text content from various document types
 */
export async function extractTextFromFile(file: File): Promise<ExtractedDocument> {
  const fileType = file.type;
  const fileName = file.name;
  const fileSize = file.size;
  let extractedText = '';

  try {
    // PDF files
    if (fileType === 'application/pdf') {
      extractedText = await extractTextFromPdf(file);
    } 
    // Word documents
    else if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
             fileType === 'application/msword') {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      extractedText = result.value || '';
    } 
    // Text files
    else if (fileType === 'text/plain') {
      extractedText = await file.text();
    } 
    else {
      throw new Error(`Unsupported file type: ${fileType}`);
    }

    return {
      originalFileName: fileName,
      originalFileType: fileType,
      originalFileSize: fileSize,
      extractedText
    };
  } catch (error) {
    console.error(`Error extracting text from ${fileName}:`, error);
    throw new Error(`Failed to extract text from ${fileName}: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Validate if the file type is supported for text extraction
 */
export function isFileTypeSupported(file: File): boolean {
  const supportedTypes = [
    'application/pdf',                                              // PDF
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
    'application/msword',                                           // DOC
    'text/plain'                                                    // TXT
  ];
  
  return supportedTypes.includes(file.type);
}
